import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SurveyQuestionTypePicker } from '../../components/customerSuccess/SurveyQuestionTypePicker';
import { SurveyTypePicker } from '../../components/customerSuccess/SurveyTypePicker';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { CompanyMultiSelectCombo } from '../../components/ui/CompanyMultiSelectCombo';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { RequiredMark } from '../../components/ui/RequiredMark';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { Company, listCompanies } from '../../services/coreCompaniesApi';
import {
  createSurveyForm,
  createSurveyFormTestPreview,
  deleteSurveyForm,
  listSurveyForms,
  sendSurveyFormTest,
  SurveyForm,
  SurveyFormUpsert,
  SurveyQuestion,
  updateSurveyForm,
} from '../../services/customerSuccessEngagementApi';
import { EmailTemplate, listEmailTemplates } from '../../services/emailTemplatesApi';
import { matchesTemplateKind } from '../../utils/matchesTemplateKind';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';

function labeledScaleExpectedCount(min: number, max: number): number {
  return max - min + 1;
}

function normalizeLabeledScaleLabels(labels: string[]): string[] {
  return labels.map((label) => label.trim()).filter(Boolean);
}

function formatSurveyFormCompanies(item: SurveyForm): string {
  if (!item.companies?.length) {
    return 'Formulário padrão';
  }
  return item.companies.map((company) => company.name).join(', ');
}

function labeledScaleConfigMessage(
  questionIndex: number,
  min: number,
  max: number,
  filled: number,
  expected: number,
): string {
  const delta = expected - filled;
  const adjustment =
    delta > 0
      ? `Inclua mais ${delta} rótulo${delta === 1 ? '' : 's'} ou reduza o intervalo mín/máx.`
      : `Remova ${Math.abs(delta)} rótulo${Math.abs(delta) === 1 ? '' : 's'} ou amplie o intervalo mín/máx.`;

  return (
    `Pergunta ${questionIndex + 1}: a régua de ${min} a ${max} precisa de exatamente ${expected} ` +
    `rótulos (hoje: ${filled}). ${adjustment} ` +
    'Cada linha é um valor da escala — para vários critérios, use várias perguntas.'
  );
}

type FormState = SurveyFormUpsert;

function emptyQuestion(type: SurveyQuestion['type'] = 'Stars'): SurveyQuestion {
  if (type === 'NumericScale') {
    return {
      id: crypto.randomUUID().replace(/-/g, ''),
      prompt: '',
      type: 'NumericScale',
      min: 0,
      max: 10,
      labels: [],
      allowComment: false,
      required: true,
    };
  }

  if (type === 'LabeledScale') {
    return {
      id: crypto.randomUUID().replace(/-/g, ''),
      prompt: '',
      type: 'LabeledScale',
      min: 1,
      max: 5,
      labels: ['Muito ruim', 'Ruim', 'Regular', 'Bom', 'Excelente'],
      allowComment: false,
      required: true,
    };
  }

  return {
    id: crypto.randomUUID().replace(/-/g, ''),
    prompt: '',
    type: 'Stars',
    min: 1,
    max: 5,
    labels: [],
    allowComment: false,
    required: true,
  };
}

const emptyForm: FormState = {
  name: '',
  surveyType: 'Csat',
  companyIds: [],
  questions: [emptyQuestion('Stars')],
  allowGeneralComment: true,
  emailTemplateId: '',
  isActive: true,
};

export function SurveyFormsPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('customer-success.engagement');
  const [items, setItems] = useState<SurveyForm[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();

  const templatesForType = useMemo(
    () =>
      templates.filter(
        (item) => item.isActive && matchesTemplateKind(item.kind, form.surveyType),
      ),
    [templates, form.surveyType],
  );

  async function loadData(): Promise<void> {
    setLoading(true);
    try {
      const [list, companyResult, templateList] = await Promise.all([
        listSurveyForms(),
        listCompanies({ activeOnly: true, page: 1, pageSize: 200 }),
        listEmailTemplates('customer-success'),
      ]);
      setItems(list);
      setCompanies(companyResult.items);
      setTemplates(templateList);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar formulários.'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  function startCreate(): void {
    setEditingId(null);
    const surveyType = form.surveyType || 'Csat';
    const matching = templates.filter(
      (item) => item.isActive && matchesTemplateKind(item.kind, surveyType),
    );
    setForm({
      ...emptyForm,
      surveyType,
      questions: [emptyQuestion(surveyType === 'Nps' ? 'NumericScale' : 'Stars')],
      emailTemplateId: matching[0]?.id ?? '',
    });
    expandForm();
  }

  function startEdit(item: SurveyForm): void {
    setEditingId(item.id);
    setForm({
      name: item.name,
      surveyType: item.surveyType,
      companyIds: item.companyIds ?? item.companies?.map((company) => company.id) ?? [],
      questions: item.questions.map((question) => ({
        ...question,
        labels: question.labels ?? [],
      })),
      allowGeneralComment: item.allowGeneralComment,
      emailTemplateId: item.emailTemplateId ?? '',
      isActive: item.isActive,
    });
    expandForm();
  }

  function setCompanyIds(companyIds: string[]): void {
    setForm((prev) => ({ ...prev, companyIds }));
  }

  function handleSurveyTypeChange(surveyType: string): void {
    setForm((prev) => {
      const nextQuestions =
        prev.questions.length === 1 && !prev.questions[0].prompt.trim()
          ? [emptyQuestion(surveyType === 'Nps' ? 'NumericScale' : 'Stars')]
          : prev.questions;
      const matching = templates.filter(
        (item) => item.isActive && matchesTemplateKind(item.kind, surveyType),
      );
      const keepCurrent = matching.some((item) => item.id === prev.emailTemplateId);
      return {
        ...prev,
        surveyType,
        questions: nextQuestions,
        emailTemplateId: keepCurrent ? prev.emailTemplateId : matching[0]?.id ?? '',
      };
    });
  }

  function showTestWarnings(warnings: string[] | undefined): void {
    for (const warning of warnings ?? []) {
      toast.info(warning);
    }
  }

  function resolveTestRecipientLabel(): string {
    const override = testEmail.trim();
    if (override) {
      return override;
    }
    return 'o e-mail do usuário logado';
  }

  async function handleOpenTestPreview(item: SurveyForm): Promise<void> {
    setTestingId(item.id);
    try {
      const preview = await createSurveyFormTestPreview(
        item.id,
        testEmail.trim() ? { email: testEmail.trim() } : undefined,
      );
      showTestWarnings(preview.warnings);
      window.open(preview.relativePath, '_blank', 'noopener,noreferrer');
      toast.success('Página pública de teste aberta. Responda para validar o recebimento.');
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao gerar teste do formulário.'));
    } finally {
      setTestingId(null);
    }
  }

  async function handleSendTestEmail(item: SurveyForm): Promise<void> {
    const recipientLabel = resolveTestRecipientLabel();
    const ok = await confirm({
      title: 'Enviar e-mail de teste',
      message:
        `Enviar o e-mail de teste do formulário "${item.name}" para ${recipientLabel}? `
        + 'É necessário ter a URL pública do app configurada em Configurações do sistema.',
      confirmLabel: 'Enviar e-mail',
    });
    if (!ok) return;

    setTestingId(item.id);
    try {
      const result = await sendSurveyFormTest(
        item.id,
        testEmail.trim() ? { email: testEmail.trim() } : undefined,
      );
      showTestWarnings(result.warnings);
      toast.success(`E-mail de teste enviado para ${result.recipientEmail}.`);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao enviar e-mail de teste.'));
    } finally {
      setTestingId(null);
    }
  }

  function updateQuestion(index: number, patch: Partial<SurveyQuestion>): void {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((question, i) =>
        i === index ? { ...question, ...patch } : question,
      ),
    }));
  }

  function changeQuestionType(index: number, type: SurveyQuestion['type']): void {
    const base = emptyQuestion(type);
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((question, i) =>
        i === index
          ? {
              ...base,
              id: question.id,
              prompt: question.prompt,
              allowComment: question.allowComment,
              required: question.required,
            }
          : question,
      ),
    }));
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite || !form.name.trim() || form.questions.length === 0) return;
    if (!form.emailTemplateId) {
      toast.info('Selecione um template de e-mail.');
      return;
    }

    for (let index = 0; index < form.questions.length; index += 1) {
      const question = form.questions[index];
      if (question.type !== 'LabeledScale') continue;

      if (!Number.isFinite(question.min) || !Number.isFinite(question.max) || question.max < question.min) {
        toast.info(
          `Pergunta ${index + 1}: na régua com rótulos, o máximo precisa ser maior ou igual ao mínimo. Ajuste o intervalo e tente de novo.`,
        );
        return;
      }

      const expected = labeledScaleExpectedCount(question.min, question.max);
      const labels = normalizeLabeledScaleLabels(question.labels);
      if (labels.length !== expected) {
        toast.info(
          labeledScaleConfigMessage(index, question.min, question.max, labels.length, expected),
        );
        return;
      }
    }

    setSaving(true);
    try {
      const payload: SurveyFormUpsert = {
        ...form,
        name: form.name.trim(),
        companyIds: form.companyIds,
        emailTemplateId: form.emailTemplateId,
        questions: form.questions.map((question) => ({
          ...question,
          prompt: question.prompt.trim(),
          labels:
            question.type === 'LabeledScale'
              ? normalizeLabeledScaleLabels(question.labels)
              : [],
        })),
      };

      if (editingId) {
        await updateSurveyForm(editingId, payload);
        toast.success('Formulário atualizado.');
        collapseForm(() => {
          setEditingId(null);
          setForm(emptyForm);
        });
      } else {
        await createSurveyForm(payload);
        toast.success('Formulário criado.');
        handleCreateSuccess(() => {
          setEditingId(null);
          setForm(emptyForm);
        });
      }
      setEditingId(null);
      await loadData();
    } catch (error) {
      const message = getRequestErrorMessage(error, 'Falha ao salvar formulário.');
      const isLabeledScaleConfig =
        /rótulo/i.test(message) || /LabeledScale/i.test(message);
      if (isLabeledScaleConfig) {
        toast.info(message);
      } else {
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: SurveyForm): Promise<void> {
    if (!canWrite) return;
    const ok = await confirm({
      title: 'Remover formulário',
      message: `Remover "${item.name}"?`,
    });
    if (!ok) return;
    try {
      await deleteSurveyForm(item.id);
      toast.success('Formulário removido.');
      await loadData();
    } catch {
      toast.error('Falha ao remover.');
    }
  }

  const hasLabeledScaleConfigIssue = form.questions.some((question) => {
    if (question.type !== 'LabeledScale') return false;
    if (!Number.isFinite(question.min) || !Number.isFinite(question.max) || question.max < question.min) {
      return true;
    }
    return (
      normalizeLabeledScaleLabels(question.labels).length !==
      labeledScaleExpectedCount(question.min, question.max)
    );
  });

  return (
    <PageShell>
      <PageHeader
        title="Formulários de pesquisa"
        description="CSAT e NPS por empresa ou como formulário padrão. Sem formulário, o sistema usa perguntas padrão. Use Testar para validar o envio e o preenchimento antes do cliente."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/customer-success/settings?tab=emails" className="erp-btn-secondary">
              Templates de e-mail
            </Link>
            <Link to="/customer-success/engagement" className="erp-btn-secondary">
              Respostas prontas
            </Link>
            {canWrite ? (
              <button type="button" className="erp-btn-primary" onClick={startCreate}>
                Novo formulário
              </button>
            ) : null}
          </div>
        }
      />

      {canWrite ? (
        <div className="mb-4 space-y-2 rounded-xl border border-secondary-200 bg-white px-4 py-3">
          <label className="erp-label min-w-[16rem] max-w-xl">
            E-mail para teste (opcional)
            <input
              className="erp-input"
              type="email"
              value={testEmail}
              placeholder="Ex.: seu.email@empresa.com"
              onChange={(event) => setTestEmail(event.target.value)}
            />
          </label>
          <p className="text-xs text-secondary-500">
            Se preenchido, <strong>Abrir página</strong> e <strong>Enviar e-mail</strong> usam este
            endereço. Se vazio, o e-mail de teste vai para o usuário logado. O envio exige a URL
            pública do app em Configurações do sistema (Sucesso do Cliente); sem ela, o e-mail não é
            disparado.
          </p>
        </div>
      ) : null}
      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar formulário' : 'Novo formulário'}
          expanded={formExpanded}
          onToggle={() =>
            toggleOrCancel(() => {
              setEditingId(null);
              setForm(emptyForm);
            })
          }
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form-grid">
            <label className="erp-label erp-form-grid-full">
              Nome <RequiredMark />
              <input
                className="erp-input"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                required
              />
            </label>
            <div className="erp-label erp-form-grid-full">
              <span>
                Tipo <RequiredMark />
              </span>
              <div className="mt-1">
                <SurveyTypePicker value={form.surveyType} onChange={handleSurveyTypeChange} />
              </div>
            </div>
            <label className="erp-label erp-form-grid-full">
              Template de e-mail <RequiredMark />
              <select
                className="erp-input"
                value={form.emailTemplateId}
                onChange={(event) => setForm({ ...form, emailTemplateId: event.target.value })}
                required
              >
                <option value="">Selecione</option>
                {templatesForType.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
              {templatesForType.length === 0 ? (
                <span className="erp-hint mt-1 block">
                  Nenhum template {form.surveyType} ativo.{' '}
                  <Link to="/customer-success/settings?tab=emails" className="underline">
                    Cadastre em Configurações → E-mails
                  </Link>
                  .
                </span>
              ) : null}
            </label>
            <div className="erp-label erp-form-grid-full">
              <span>Empresas (opcional)</span>
              <p className="mb-2 text-xs font-normal text-secondary-500">
                Vazio = formulário padrão. Use &quot;Todas as empresas&quot; no combo para marcar em
                massa, ou selecione só as que tiverem regra própria.
              </p>
              <CompanyMultiSelectCombo
                options={companies}
                value={form.companyIds}
                onChange={setCompanyIds}
                emptyLabel="Formulário padrão"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.allowGeneralComment}
                onChange={(event) =>
                  setForm({ ...form, allowGeneralComment: event.target.checked })
                }
              />
              Comentário geral
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
              />
              Ativo
            </label>

            <div className="erp-form-grid-full space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-secondary-800">Perguntas</p>
                {form.questions.length < 10 ? (
                  <button
                    type="button"
                    className="text-sm text-primary-700 hover:underline"
                    onClick={() =>
                      setForm({
                        ...form,
                        questions: [
                          ...form.questions,
                          emptyQuestion(form.surveyType === 'Nps' ? 'NumericScale' : 'Stars'),
                        ],
                      })
                    }
                  >
                    Adicionar pergunta
                  </button>
                ) : null}
              </div>

              {form.questions.map((question, index) => {
                const labeledExpected =
                  question.type === 'LabeledScale'
                    ? labeledScaleExpectedCount(question.min, question.max)
                    : 0;
                const labeledFilled =
                  question.type === 'LabeledScale'
                    ? normalizeLabeledScaleLabels(question.labels).length
                    : 0;
                const labeledInvalid =
                  question.type === 'LabeledScale' &&
                  labeledExpected > 0 &&
                  labeledFilled !== labeledExpected;

                return (
                <div
                  key={question.id}
                  className="space-y-2 rounded-lg border border-secondary-200 p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-medium text-secondary-500">Pergunta {index + 1}</p>
                    {form.questions.length > 1 ? (
                      <button
                        type="button"
                        className="text-xs text-red-600 hover:underline"
                        onClick={() =>
                          setForm({
                            ...form,
                            questions: form.questions.filter((_, i) => i !== index),
                          })
                        }
                      >
                        Remover
                      </button>
                    ) : null}
                  </div>
                  <label className="erp-label">
                    Enunciado <RequiredMark />
                    <input
                      className="erp-input"
                      value={question.prompt}
                      onChange={(event) =>
                        updateQuestion(index, { prompt: event.target.value })
                      }
                      required
                    />
                  </label>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-secondary-800">Tipo da pergunta</p>
                    <SurveyQuestionTypePicker
                      value={question.type}
                      onChange={(type) => changeQuestionType(index, type)}
                    />
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <label className="erp-label">
                      Mín
                      <input
                        className="erp-input"
                        type="number"
                        value={question.min}
                        onChange={(event) =>
                          updateQuestion(index, { min: Number(event.target.value) })
                        }
                      />
                    </label>
                    <label className="erp-label">
                      Máx
                      <input
                        className="erp-input"
                        type="number"
                        value={question.max}
                        onChange={(event) =>
                          updateQuestion(index, { max: Number(event.target.value) })
                        }
                      />
                    </label>
                  </div>
                  {question.type === 'LabeledScale' ? (
                    <div className="space-y-2">
                      <div
                        className={`rounded-lg border px-3 py-2 text-xs ${
                          labeledInvalid
                            ? 'border-amber-200 bg-amber-50 text-amber-950'
                            : 'border-secondary-200 bg-secondary-50 text-secondary-700'
                        }`}
                      >
                        <p className="font-medium">
                          {labeledInvalid
                            ? 'Ajuste a configuração da régua'
                            : 'Como funciona a régua com rótulos'}
                        </p>
                        <ul className="mt-1 list-disc space-y-0.5 pl-4">
                          <li>
                            Informe exatamente {labeledExpected} rótulos: um para cada valor de{' '}
                            {question.min} a {question.max}.
                          </li>
                          <li>
                            Exemplo (1–5): Muito ruim, Ruim, Regular, Bom, Excelente.
                          </li>
                          <li>
                            Não liste critérios diferentes aqui (qualidade, clareza, tempo…). Nesse
                            caso, adicione uma pergunta para cada critério.
                          </li>
                        </ul>
                        <p className="mt-2 font-medium">
                          {labeledInvalid
                            ? `Situação atual: ${labeledFilled} de ${labeledExpected} rótulos. ${
                                labeledExpected - labeledFilled > 0
                                  ? `Faltam ${labeledExpected - labeledFilled}.`
                                  : `Há ${labeledFilled - labeledExpected} a mais.`
                              }`
                            : `Configuração ok: ${labeledFilled} de ${labeledExpected} rótulos.`}
                        </p>
                      </div>
                      <label className="erp-label">
                        Rótulos da régua (um valor por linha)
                        <textarea
                          className="erp-input min-h-[5rem]"
                          value={question.labels.join('\n')}
                          onChange={(event) =>
                            updateQuestion(index, {
                              labels: event.target.value.split('\n'),
                            })
                          }
                          placeholder={'Muito ruim\nRuim\nRegular\nBom\nExcelente'}
                        />
                      </label>
                      {labeledExpected > 0 && labeledExpected <= 20 ? (
                        <ol className="erp-hint m-0 list-none space-y-0.5 p-0">
                          {Array.from({ length: labeledExpected }, (_, offset) => {
                            const value = question.min + offset;
                            const label = question.labels[offset]?.trim() ?? '';
                            return (
                              <li key={value}>
                                Valor {value}: {label || '(sem rótulo)'}
                              </li>
                            );
                          })}
                        </ol>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={question.required}
                        onChange={(event) =>
                          updateQuestion(index, { required: event.target.checked })
                        }
                      />
                      Obrigatória
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={question.allowComment}
                        onChange={(event) =>
                          updateQuestion(index, { allowComment: event.target.checked })
                        }
                      />
                      Comentário na pergunta
                    </label>
                  </div>
                </div>
                );
              })}
            </div>

            {hasLabeledScaleConfigIssue ? (
              <p className="erp-form-grid-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                Há régua(s) com rótulos incompletos. Isso não é uma falha do sistema: complete a
                quantidade de rótulos ou ajuste mín/máx antes de salvar. Para vários critérios,
                use várias perguntas.
              </p>
            ) : null}

            <FormActionBar
              className="erp-btn-actions erp-form-grid-full"
              cancelLabel="Cancelar"
              onCancel={() =>
                toggleOrCancel(() => {
                  setEditingId(null);
                  setForm(emptyForm);
                })
              }
            >
              <button type="submit" className="erp-btn-primary" disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      {loading ? (
        <LoadingSpinner />
      ) : items.length === 0 ? (
        <p className="text-sm text-secondary-500">Nenhum formulário cadastrado.</p>
      ) : (
        <ul className="divide-y divide-secondary-100 rounded-xl border border-secondary-200 bg-white">
          {items.map((item) => (
            <li key={item.id} className="flex items-start justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="font-medium text-secondary-900">
                  {item.name}
                  {!item.isActive ? (
                    <span className="ml-2 text-xs font-normal text-secondary-500">(inativo)</span>
                  ) : null}
                </p>
                <p className="mt-1 text-xs text-secondary-500">
                  {item.surveyType.toUpperCase()} · {formatSurveyFormCompanies(item)} ·{' '}
                  {item.questions.length} pergunta{item.questions.length === 1 ? '' : 's'}
                  {item.emailTemplateName ? ` · e-mail: ${item.emailTemplateName}` : ''}
                </p>
              </div>
              {canWrite ? (
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                  <button
                    type="button"
                    className="erp-btn-secondary px-2 py-1 text-xs"
                    disabled={testingId === item.id}
                    onClick={() => void handleOpenTestPreview(item)}
                  >
                    {testingId === item.id ? '...' : 'Abrir página'}
                  </button>
                  <button
                    type="button"
                    className="erp-btn-secondary px-2 py-1 text-xs"
                    disabled={testingId === item.id}
                    onClick={() => void handleSendTestEmail(item)}
                  >
                    Enviar e-mail
                  </button>
                  <EditIconButton onClick={() => startEdit(item)} />
                  <DeleteIconButton onClick={() => void handleDelete(item)} />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </PageShell>
  );
}
